import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form8582Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 8582 Explained: Passive Activity Loss for Form 5227"
          description="Learn how to complete Form 8582 for passive activity loss limits. Step-by-step guidance for Form 5227 filers to report allowed and disallowed losses correctly."
          keywords=""
          canonical="resources/tax-exempt/form-8582-instructions/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight"> Form 8582 </span>
          </span>{' '}
          Passive Activity Loss Limitations
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Report your passive losses quickly and accurately!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8582instructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-8582-instructions/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href="https://www.taxzerone.com/"
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 8582 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="1"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Form 8582?
                        </strong>{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 8582, Passive Activity Loss Limitations
                        </strong>
                        , is used to calculate{' '}
                        <strong style={{ fontWeight: 600 }}>
                          passive activity losses (PALs)
                        </strong>{' '}
                        and determine how much of those losses can be allowed in
                        the current tax year.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="2"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Uses Form 8582?
                        </strong>{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Individuals, estates, and trusts
                        </strong>{' '}
                        generally use Form 8582 when they have passive activity
                        deductions, including{' '}
                        <strong style={{ fontWeight: 600 }}>
                          prior-year unallowed passive losses
                        </strong>
                        .
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="3"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Does Form 8582 Calculate?
                        </strong>{' '}
                        The form determines{' '}
                        <strong style={{ fontWeight: 600 }}>
                          current-year passive activity losses, prior-year
                          unallowed losses, allowable losses, and losses carried
                          forward
                        </strong>{' '}
                        under the passive activity rules.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="4"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Activities Are Generally Passive?
                        </strong>{' '}
                        Passive activities generally include{' '}
                        <strong style={{ fontWeight: 600 }}>
                          rental activities
                        </strong>{' '}
                        and trade or business activities in which the taxpayer{' '}
                        <strong style={{ fontWeight: 600 }}>
                          does not materially participate
                        </strong>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="4"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Trust and Estate Filing:
                        </strong>{' '}
                        Form 8582 may apply when an{' '}
                        <strong style={{ fontWeight: 600 }}>
                          estate or trust
                        </strong>{' '}
                        has passive activity deductions subject to the passive
                        loss limitations. Corporations generally use{' '}
                        <strong style={{ fontWeight: 600 }}>Form 8810</strong>{' '}
                        instead.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Form 8582</span> is used to
                  calculate how much of a trust’s{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    passive activity losses
                  </span>{' '}
                  - such as{' '}
                  <span style={{ fontWeight: 600 }}>
                    rental real estate or royalty losses
                  </span>{' '}
                  - can be deducted for the current tax year. Because the IRS
                  limits passive loss deductions, the form helps separate the
                  losses that are allowable now from the suspended losses that
                  must be{' '}
                  <span style={{ fontWeight: 600 }}>carried forward.</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  When{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-5227/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-filing Form 5227
                  </Link>{' '}
                  filers report allowable rental real estate losses on{' '}
                  <Link
                    href="/resources/tax-exempt/form-5227-schedule-e/"
                    sx={{ color: '#0202ff' }}
                  >
                    Schedule E (Form 1040)
                  </Link>
                  , <span style={{ fontWeight: 600 }}>Part I, Line 22.</span>{' '}
                  This amount may include rental losses from prior years that
                  become deductible after applying the{' '}
                  <span style={{ fontWeight: 600 }}>passive activity loss</span>{' '}
                  limitations.{' '}
                  <span style={{ fontWeight: 600 }}>Form 8582</span> is
                  completed only when necessary to calculate the allowable loss.
                  <span style={{ fontWeight: 600 }}> Line 28</span> is used
                  solely to disclose certain prior-year unallowed losses that
                  are not reported on Form 8582 and does not allow a deduction.
                  Royalty losses should not be reported on{' '}
                  <span style={{ fontWeight: 600 }}>Line 22.</span>
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 8582 Attachment?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#under" sx={{ color: '#0202ff' }}>
                          Understanding Passive Activity Losses (PALs)
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must Attach Form 8582?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How Form 8582 Relates to Schedule E (Form 1040)
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to File Form 8582 Attachment with TaxZerone
                        </Link>
                        <List>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part1" sx={{ color: '#0202ff' }}>
                                Part I: 2025 Passive Activity Loss
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part2" sx={{ color: '#0202ff' }}>
                                Part II: Special Allowance for Rental Real
                                Estate Activities with Active Participation
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part3" sx={{ color: '#0202ff' }}>
                                Part III: Total Losses Allowed
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part4" sx={{ color: '#0202ff' }}>
                                Part IV: Complete This Part Before Part I, Lines
                                1a, 1b, and 1c
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part5" sx={{ color: '#0202ff' }}>
                                Part V: Complete This Part Before Part I, Lines
                                2a, 2b, and 2c
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part6" sx={{ color: '#0202ff' }}>
                                Part VI: Use This Part if an Amount Is Shown on
                                Part II, Line 9
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part7" sx={{ color: '#0202ff' }}>
                                Part VII: Allocation of Unallowed Losses
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part8" sx={{ color: '#0202ff' }}>
                                Part VIII: Allowed Losses
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part9" sx={{ color: '#0202ff' }}>
                                Part IX: Activities With Losses Reported on Two
                                or More Forms or Schedules
                              </Link>
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItemText>
                    </ListItem>

                    <ListItem
                      sx={{ py: 0.5, alignItems: 'flex-start', mt: -2 }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#add" sx={{ color: '#0202ff' }}>
                          Additional IRS References
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whatis"
                >
                  What is Form 8582 Attachment?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <span style={{ fontWeight: 600 }}> Form 8582</span> is an IRS
                  form used to determine how much of a trust’s passive activity
                  losses - such as rental real estate or royalty losses - can be
                  deducted in the current tax year. It also tracks{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    carried forward passive losses
                  </span>{' '}
                  from prior years, helping show which losses are allowed now
                  and which must remain suspended for future use.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="under"
                >
                  Understanding Passive Activity Losses (PAL)
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Passive activity loss limitations
                  </span>{' '}
                  happen when the expenses from a rental or other passive
                  activity are greater than the income it earns. The IRS does
                  not allow trusts to deduct all passive losses at once, so the
                  trust must use{' '}
                  <span style={{ fontWeight: 600 }}> 8582 form</span> to
                  determine the allowable portion for the year.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who must Attach Form 8582?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Any
                  <Link
                    href="/resources/tax-exempt/form-5227-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 5227
                  </Link>{' '}
                  filer who reports rental income, royalty income, or other
                  passive activity losses and shows a net loss must attach{' '}
                  <span style={{ fontWeight: 600 }}> 8582 form</span>. This
                  includes{' '}
                  <span style={{ fontWeight: 600 }}>split-interest trusts</span>{' '}
                  with rental properties, passive investments, or{' '}
                  <span style={{ fontWeight: 600 }}>suspended losses</span> from
                  prior years.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="how"
                >
                  How Form 8582 Relates to Schedule E (Form 1040)
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If you{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-5227/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 5227
                  </Link>
                  , any allowable rental real estate loss for the year is
                  reported on{' '}
                  <Link
                    href="/resources/tax-exempt/form-5227-schedule-e/"
                    sx={{ color: '#0202ff' }}
                  >
                    Schedule E (Form 1040)
                  </Link>
                  , <span style={{ fontWeight: 600 }}>Part I, Line 22.</span>{' '}
                  This can include rental losses{' '}
                  <span style={{ fontWeight: 600 }}>
                    carried forward from prior years
                  </span>{' '}
                  once they are allowed under the passive activity loss rules.
                  Form 8582 is only completed when it’s needed to figure out how
                  much of the loss you can claim.{' '}
                  <span style={{ fontWeight: 600 }}>Line 28</span> is used to
                  show certain prior-year losses for disclosure only and does
                  not affect your deduction. Royalty losses are not entered on
                  Line 22.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="howto"
                >
                  How to File Form 8582 Attachment with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  TaxZerone allows you to upload{' '}
                  <span style={{ fontWeight: 600 }}> Form 8582</span> as an
                  attachment when you{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-5227/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 5227
                  </Link>
                  . The system makes sure the attachment is included, providing
                  the IRS with a complete and accurate picture of your passive
                  income, allowable losses, and any losses that were suspended.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 84,
                  }}
                  id="part1"
                >
                  Part I: 2025 Passive Activity Loss
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8582-part1-passive-img.webp" />
                  <source srcSet="/assets/images/f8582-part1-passive-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8582-part1-passive-img.png"
                    loading="lazy"
                    alt="Form 8582 Part I Line 1a-3: Passive Activity Loss and Passive Activity Loss Limitations"
                    width={760}
                    height={473}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Part I</span> is where
                  you&apos;ll combine net income and net losses from all passive
                  activities to see if a{' '}
                  <span style={{ fontWeight: 600 }}>
                    Passive Activity Loss (PAL)
                  </span>{' '}
                  applies for 2025. Before filling out Part I, you&apos;ll need
                  to calculate the necessary amounts using{' '}
                  <span style={{ fontWeight: 600 }}>Parts IV and V.</span>
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Part IV</span> is for
                    rental real estate activities where you actively
                    participate.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Part V</span> applies to
                    all other passive activities.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Rental Real Estate Activities with Active Participation
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 1a:</span> Enter total
                  net income from activities shown in{' '}
                  <span style={{ fontWeight: 600 }}> Part IV, column (a).</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 1b:</span> Enter total
                  net losses from{' '}
                  <span style={{ fontWeight: 600 }}> Part IV, column (b).</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 1c:</span> Enter prior
                  years’ unallowed losses from{' '}
                  <span style={{ fontWeight: 600 }}> Part IV, column (c).</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Line 1d:</span> Combine the
                  amounts from{' '}
                  <span style={{ fontWeight: 600 }}>lines 1a, 1b, and 1c</span>{' '}
                  to determine the total.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  All other passive activities (from Part V)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 2a:</span> Enter total
                  activities with net income from Part V, column (a).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 2b:</span> Enter total
                  activities with net loss from Part V, column (b).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 2c:</span> Enter prior
                  year unallowed losses from Part V, column (c).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 2d:</span> Combine
                  amounts from lines 2a, 2b, and 2c.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3: Overall passive gain or loss
                </Typography>
                <Typography variant="body1">
                  Combine{' '}
                  <span style={{ fontWeight: 600 }}>lines 1d and 2d,</span> then
                  subtract any{' '}
                  <span style={{ fontWeight: 600 }}>
                    prior year unallowed Commercial Revitalization Deduction
                    (CRD).
                  </span>{' '}
                  Enter the final amount on line 3.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If the result is{' '}
                    <span style={{ fontWeight: 600 }}>zero or more,</span> stop
                    here. Attach this form to your return - all passive losses
                    are allowed, including prior-year unallowed losses reported
                    on lines <span style={{ fontWeight: 600 }}>1c or 2c.</span>{' '}
                    Report the losses on the applicable forms and schedules as
                    usual.{' '}
                  </li>
                  <li>
                    If <span style={{ fontWeight: 600 }}>loss:</span>{' '}
                  </li>
                  <ul>
                    <li>
                      If <span style={{ fontWeight: 600 }}>line 1d</span> is a
                      loss, continue to Part II.{' '}
                    </li>
                    <li>
                      If <span style={{ fontWeight: 600 }}>line 2d</span> is a
                      loss (and line 1d is zero or positive), skip{' '}
                      <span style={{ fontWeight: 600 }}>Part II</span> and go
                      directly to{' '}
                      <span style={{ fontWeight: 600 }}>line 10.</span>{' '}
                    </li>
                  </ul>
                </Box>
                <Alert
                  severity="warning"
                  icon={<NoteAltIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Notes:</b> If your filing status is Married Filing
                  Separately and you lived with your spouse at any time during
                  the year, do not complete Part II. Go directly to line 10.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you have a prior year unallowed{' '}
                  <span style={{ fontWeight: 600 }}> CRD</span> from rental real
                  estate activities, treat it as a{' '}
                  <span style={{ fontWeight: 600 }}>negative amount.</span>{' '}
                  Combine it with lines 1d and 2d, enter the total on line 3,
                  and write <span style={{ fontWeight: 600 }}>“CRD”</span>
                  with the negative amount on the dotted line.
                </Typography>
                <Alert
                  severity="warning"
                  icon={<NoteAltIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Notes:</b> If you included prior year unallowed CRD from
                  rental real estate activities on line 3, and line 3 shows a
                  loss while line 1d is zero or positive, follow the
                  instructions for Part II, line 9.
                </Alert>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Tips:</b> If you need extra space for Parts IV through IX,
                  attach copies of the relevant Form 8582 pages or include a
                  separate schedule formatted for the same as the applicable
                  part.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 84,
                  }}
                  id="part2"
                >
                  Part II: Special Allowance for Rental Real Estate Activities
                  with Active Participation
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8582-part2-special-img.webp" />
                  <source srcSet="/assets/images/f8582-part2-special-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8582-part2-special-img.png"
                    loading="lazy"
                    alt="Form 8582 Part II: Special Allowance for Rental Real Estate Activities with Active Participation"
                    width={760}
                    height={166}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Line 4: Enter the smaller loss
                </Typography>
                <Typography variant="body1">
                  On line 4 of Part II, enter the smaller of:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    The loss on{' '}
                    <span style={{ fontWeight: 600 }}>Part I, line 1d,</span> or
                  </li>
                  <li>
                    The loss on <span style={{ fontWeight: 600 }}>line 3.</span>
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#c026521f',
                    borderLeft: '5px solid #c02652',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, fontStyle: 'italic' }}
                  >
                    Example:{' '}
                  </Typography>
                  <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                    <li>Part I, line 1d, shows a loss of $3,000. </li>
                    <li>Line 2d shows a gain of $100. </li>
                    <li>The combined loss on line 3 is $2,900. </li>
                  </Box>
                  <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                    Since $2,900 is smaller than $3,000, you enter{' '}
                    <span style={{ fontWeight: 600 }}> $2,900</span> as a
                    positive number on{' '}
                    <span style={{ fontWeight: 600 }}>Part II, line 4.</span>
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Line 5: Special Income Limit for Married Filing Separately
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you file as{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Married Filing Separately
                  </span>{' '}
                  and live{' '}
                  <span style={{ fontWeight: 600 }}>
                    apart from your spouse for the entire year,
                  </span>{' '}
                  enter <span style={{ fontWeight: 600 }}>$75,000</span> on line
                  5 instead of{' '}
                  <span style={{ fontWeight: 600 }}>$150,000.</span>
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Line 6: Modified adjusted gross income (MAGI)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter your{' '}
                  <span style={{ fontWeight: 600 }}>
                    Modified adjusted gross income (MAGI),
                  </span>{' '}
                  then <span style={{ fontWeight: 600 }}>remove</span> the
                  following amounts if they are included:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Passive activity losses </li>
                  <li>
                    Rental real estate losses allowed to real estate
                    professionals{' '}
                  </li>
                  <li>
                    Taxable Social Security or Tier 1 railroad retirement
                    benefits{' '}
                  </li>
                  <li>
                    Deductible traditional IRA and section 501(c)(18) pension
                    contributions{' '}
                  </li>
                  <li>The deductible part of self-employment tax </li>
                  <li>
                    Excluded interest from EE and I U.S. savings bonds used for
                    education{' '}
                  </li>
                  <li>Employer-provided adoption assistance exclusions</li>
                  <li>Student loan interest deduction</li>
                  <li>FDII and GILTI deductions </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Then <span style={{ fontWeight: 600 }}>add back:</span>{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Portfolio income and related expenses </li>
                  <li>
                    Any income treated as nonpassive (such as gains from PTPs or
                    recharacterized passive income){' '}
                  </li>
                  <li>
                    Any overall loss from a fully disposed passive activity{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the final amount on{' '}
                  <span style={{ fontWeight: 600 }}>line 6.</span> If the result
                  is less than zero, enter{' '}
                  <span style={{ fontWeight: 600 }}>0.</span>
                </Typography>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#c026521f',
                    borderLeft: '4px solid #c02652',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, fontStyle: 'italic' }}
                  >
                    Example:
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    If your AGI is $92,000 and includes $5,500 of taxable Social
                    Security benefits, enter{' '}
                    <span style={{ fontWeight: 600 }}> $86,500</span> on line 6.
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Line 7: Income Difference Calculation
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract <span style={{ fontWeight: 600 }}>line 6</span> from{' '}
                  <span style={{ fontWeight: 600 }}>line 5</span> and enter the
                  result on line 7.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Line 8: Special Allowance Amount Calculation
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    This calculation follows the IRS rules for the{' '}
                    <span style={{ fontWeight: 600 }}>
                      special allowance on Line 8.
                    </span>
                  </li>
                  <li>
                    You multiply Line 7 by 50% (0.50) and enter the result, but
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      the maximum you can enter is $25,000.
                    </span>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Special case for Married Filing Separately:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    If you lived apart from your spouse for the entire year, the
                    maximum is $12,500.{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<NoteAltIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Notes:</b> For more details, refer to the{' '}
                  <Link
                    href="https://www.irs.gov/pub/irs-pdf/i8582.pdf"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS Form 8582 instructions
                  </Link>
                  .
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Line 9: Allowed rental loss now{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    No prior-year unallowed CRD:
                  </span>{' '}
                  Enter the smaller of{' '}
                  <span style={{ fontWeight: 600 }}> Line 4</span> or{' '}
                  <span style={{ fontWeight: 600 }}> Line 8</span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  With prior-year unallowed CRD (from Line 3):
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    First, calculate the $25,000 special allowance for
                    current-year rental losses (Lines 4–8),{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      ignoring prior-year CRD.
                    </span>{' '}
                  </li>
                  <li>
                    If there’s a current-year loss (Line 1d) and a prior-year
                    CRD loss (Line 3), use the IRS Worksheet to determine how
                    much of the remaining allowance can apply to the prior-year
                    CRD.{' '}
                  </li>
                  <li>Enter the Worksheet result on Line 9. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Worksheet Steps (all numbers as positive amounts):
                </Typography>
                <Box
                  component="ol"
                  type="A"
                  sx={{ mb: 2, '& li': { mb: 1.2 } }}
                >
                  <li>
                    Start with $25,000 (or $12,500 if married filing separately
                    and lived apart) and subtract the smaller of{' '}
                    <span style={{ fontWeight: 600 }}>Line 4</span> or{' '}
                    <span style={{ fontWeight: 600 }}>Line 8.</span>
                  </li>
                  <li>
                    Enter your prior-year CRD loss from{' '}
                    <span style={{ fontWeight: 600 }}>Part I, Line 3.</span>
                  </li>
                  <li>
                    Subtract the smaller of{' '}
                    <span style={{ fontWeight: 600 }}>Line 4</span> or{' '}
                    <span style={{ fontWeight: 600 }}>Line 8</span> from Line B.
                  </li>
                  <li>
                    {' '}
                    Enter the <span style={{ fontWeight: 600 }}>
                      smallest
                    </span>{' '}
                    of:
                  </li>
                  <ul>
                    <li>Prior unallowed CRD </li>
                    <li>Line A </li>
                    <li>Line C </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Finally,{' '}
                  <span style={{ fontWeight: 600 }}>
                    add Line D to the smaller Line 4 or Line 8,
                  </span>{' '}
                  and enter that total on Line 9. Write “CRD” and the CRD amount
                  on the dotted line.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 84,
                  }}
                  id="part3"
                >
                  Part III: Total Losses Allowed
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f8582-part3-total-losses-img.webp" />
                  <source srcSet="/assets/images/f8582-part3-total-losses-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8582-part3-total-losses-img.png"
                    loading="lazy"
                    alt="Form 8582 Part III: Total Losses Allowed, Lines 10–11"
                    width={752}
                    height={66}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Line 10: Total Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amounts from{' '}
                  <span style={{ fontWeight: 600 }}>Line 1a</span> and{' '}
                  <span style={{ fontWeight: 600 }}>Line 2a</span> and enter the
                  sum on Line 10.
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Line 11: Total Passive Losses Allowed
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.5 } }}>
                  <li>
                    Add <span style={{ fontWeight: 600 }}>Line 9</span> and{' '}
                    <span style={{ fontWeight: 600 }}>Line 10</span> to get the
                    total losses allowed from all passive activities for 2025.
                  </li>

                  <ul>
                    <li>
                      The <span style={{ fontWeight: 600 }}>allowed loss</span>{' '}
                      to report on your tax return.{' '}
                    </li>
                    <li>
                      Any{' '}
                      <span style={{ fontWeight: 600 }}>unallowed loss</span>{' '}
                      that can be carried forward to future years.{' '}
                    </li>
                  </ul>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 84,
                  }}
                  id="part4"
                >
                  Part IV: Complete This Part Before Part I, Lines 1a, 1b, and
                  1c
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f8582-part4-complete-img.webp" />
                  <source srcSet="/assets/images/f8582-part4-complete-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8582-part4-complete-img.png"
                    loading="lazy"
                    alt="Part IV Complete This Part Before Part I, Lines 1a, 1b, and 1c. "
                    width={760}
                    height={193}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Include income or losses from rental real estate activities if
                  you{' '}
                  <span style={{ fontWeight: 600 }}>actively participated</span>{' '}
                  when calculating Part I, lines 1a–1c, of Form 8582.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Prior year unallowed losses:
                  </span>{' '}
                  Only enter them in{' '}
                  <span style={{ fontWeight: 600 }}>
                    Part IV, column (c)
                  </span>{' '}
                  if you actively participated in both the year the loss arose
                  <span style={{ fontWeight: 600 }}>and</span> the current year.
                  Otherwise, report them in{' '}
                  <span style={{ fontWeight: 600 }}>Part V.</span>
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (a): Current-Year Income
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      net income for each activity
                    </span>{' '}
                    in column (a).{' '}
                  </li>
                  <li>
                    Add all amounts in column (a) and enter the total on{' '}
                    <span style={{ fontWeight: 600 }}>
                      Part I, line 1a, of Form 8582.
                    </span>
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#c026521f',
                    borderLeft: '4px solid #c02652',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, fontStyle: 'italic' }}
                  >
                    Example:
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    If a Schedule E rental activity earns $5,000 and a Form 4797
                    gain is $2,000, enter{' '}
                    <span style={{ fontWeight: 600 }}>$7,000</span> in column
                    (a).
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (b): Current Year Loss
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      net loss for each activity
                    </span>{' '}
                    here.{' '}
                    <span style={{ fontWeight: 600 }}>
                      Do not include prior year unallowed losses.
                    </span>
                  </li>
                  <li>
                    Add the amounts in column (b) and enter the total on{' '}
                    <span style={{ fontWeight: 600 }}>Part I, line 1b</span> of
                    Form 8582.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      If an activity has both income and loss on different
                      forms,
                    </span>
                    report income in column (a) and loss in column (b).
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#c026521f',
                    borderLeft: '4px solid #c02652',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, fontStyle: 'italic' }}
                  >
                    Example:
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    Schedule E shows $1,000 income, Form 4797 shows $4,500 loss
                    †’ enter $1,000 in column (a) and $4,500 in column (b).
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (c): Prior Year Losses
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter any{' '}
                    <span style={{ fontWeight: 600 }}>
                      losses from prior years that were not allowed
                    </span>{' '}
                    for each activity.
                  </li>
                  <li>
                    You can find these amounts on{' '}
                    <span style={{ fontWeight: 600 }}>
                      your 2024 Form 8582, Part VII, column (c).
                    </span>{' '}
                  </li>
                  <li>
                    Total column (c) and enter the result on{' '}
                    <span style={{ fontWeight: 600 }}>
                      Part I, line 1c, of your 2025 Form 8582.
                    </span>
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Columns (d) and (e): Overall Gain or Loss
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Combine the amounts in columns{' '}
                  <span style={{ fontWeight: 600 }}> (a) through (c)</span> for
                  each activity. Enter the{' '}
                  <span style={{ fontWeight: 600 }}>overall gain</span> in
                  column (d) or the{' '}
                  <span style={{ fontWeight: 600 }}>
                    overall loss in column (e).
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Do <span style={{ fontWeight: 600 }}>not</span> carry these
                  amounts to Parts I, II, or III of Form 8582. They are used
                  later in the form to determine the{' '}
                  <span style={{ fontWeight: 600 }}>
                    loss allowed for the current year.
                  </span>
                </Typography>
                <Alert
                  severity="warning"
                  icon={<NoteAltIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Notes:</b> If you lived with your spouse at any time during
                  the year, you cannot use Part IV. Instead, report your rental
                  activity in Part V.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 25, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 84,
                  }}
                  id="part5"
                >
                  Part V: Complete This Part Before Part I, Lines 2a, 2b, and 2c
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8582-part5-complete-img.webp" />
                  <source srcSet="/assets/images/f8582-part5-complete-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8582-part5-complete-img.png"
                    loading="lazy"
                    alt="Form 8582 Part V: Complete before Part I, Lines 2a, 2b, and 2c"
                    width={760}
                    height={200}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1">
                  Use <span style={{ fontWeight: 600 }}>Part V</span> to
                  determine the amounts to report on{' '}
                  <span style={{ fontWeight: 600 }}>Part I, lines 2a–2c,</span>
                  for:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Passive trade or business activities,</li>
                  <li>
                    Passive rental real estate activities that{' '}
                    <span style={{ fontWeight: 600 }}>
                      don’t qualify for the special allowance,
                    </span>
                  </li>
                  <li>
                    Rental activities that are{' '}
                    <span style={{ fontWeight: 600 }}>not</span> rental in real
                    estate.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you have{' '}
                  <span style={{ fontWeight: 600 }}>
                    prior-year unallowed CRD
                  </span>{' '}
                  from passive activities other than rental real estate, include
                  it in Part V and write{' '}
                  <span style={{ fontWeight: 600 }}>“CRD”</span> next to the
                  activity name.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (a): Current Year Income
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      net income for each activity
                    </span>{' '}
                    in column (a).{' '}
                  </li>

                  <li>
                    {' '}
                    Add all amounts in this column and report the total on{' '}
                    <span style={{ fontWeight: 600 }}>
                      Part I, line 2a, of Form 8582.
                    </span>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (b): Current Year Loss
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      net income for each activity
                    </span>{' '}
                    in column (b).{' '}
                  </li>

                  <li>
                    {' '}
                    Add the amounts and report the total on{' '}
                    <span style={{ fontWeight: 600 }}>
                      Part I, line 2b, of Form 8582.
                    </span>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (c): Prior Year Unallowed Losses
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter any{' '}
                    <span style={{ fontWeight: 600 }}>
                      losses from prior years that were not allowed
                    </span>{' '}
                    for each activity.
                  </li>

                  <li>
                    These amounts come from{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Part VII, column (c), of your 2024 Form 8582.
                    </span>
                  </li>
                  <li>
                    Total column (c) and enter the result on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Part I, line 2c, of your 2025 Form 8582.
                    </span>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Columns (d) and (e): Overall Gain or Loss
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Combine the amounts in columns{' '}
                    <span style={{ fontWeight: 600 }}>(a) through (c)</span> for
                    each activity.
                  </li>

                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>overall gain</span> in
                    column <span style={{ fontWeight: 600 }}>(d)</span> or the{' '}
                    <span style={{ fontWeight: 600 }}>overall loss</span> in
                    column <span style={{ fontWeight: 600 }}>(e).</span>
                  </li>
                  <li>
                    Do <span style={{ fontWeight: 600 }}>not</span> carry these
                    amounts to Parts I, II, or III of Form 8582.
                  </li>
                  <li>
                    They are used later to determine the{' '}
                    <span style={{ fontWeight: 600 }}>
                      loss allowed for the current year.
                    </span>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Parts IV and V: Gains and Losses
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Columns <span style={{ fontWeight: 600 }}>(d)</span> and{' '}
                    <span style={{ fontWeight: 600 }}>(e)</span> show overall
                    gains or losses for each activity.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Overall gain:</span>{' '}
                    Report all amounts from columns (a) - (c) on your tax forms,
                    including Form 8582.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Overall loss:</span>{' '}
                    Allocate your allowed loss from{' '}
                    <span style={{ fontWeight: 600 }}>Part III, Line 11</span>{' '}
                    using Parts VI-IX.{' '}
                  </li>
                  <li>
                    Complete Part VI only if Part II, Line 9, has an amount;
                    otherwise, use Part VII for losses and any prior year
                    unallowed CRD.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 25, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 84,
                  }}
                  id="part6"
                >
                  Part VI: Use This Part if an Amount Is Shown on Part II, Line
                  9
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8582-part6-amount-show-img.webp" />
                  <source srcSet="/assets/images/f8582-part6-amount-show-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8582-part6-amount-show-img.png"
                    loading="lazy"
                    alt="Form 8582 Part VI: Use this part if an amount is shown on Part II, Line 9"
                    width={760}
                    height={194}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1">
                  Use <span style={{ fontWeight: 600 }}> Part VI</span> to
                  allocate the special allowance from{' '}
                  <span style={{ fontWeight: 600 }}>Part II, line 9,</span>{' '}
                  across your rental real estate activities.
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Complete a{' '}
                    <span style={{ fontWeight: 600 }}> separate Part VI</span>{' '}
                    if any allowance is applied to{' '}
                    <span style={{ fontWeight: 600 }}>
                      prior-year unallowed CRD.
                    </span>{' '}
                  </li>
                  <li>
                    List each{' '}
                    <span style={{ fontWeight: 600 }}>activity name</span> in
                    the first column and the{' '}
                    <span style={{ fontWeight: 600 }}>
                      form, schedule, and line number
                    </span>{' '}
                    where the loss is reported in the second column (list
                    multiple locations if applicable or attach a
                    statement).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  If{' '}
                  <span style={{ fontWeight: 600 }}>
                    line 9 includes no prior-year CRD,
                  </span>{' '}
                  list all activities with an overall loss from{' '}
                  <span style={{ fontWeight: 600 }}>Part IV, column (e).</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    prior-year unallowed CRD is included,
                  </span>{' '}
                  complete an{' '}
                  <span style={{ fontWeight: 600 }}>
                    additional Part IV
                  </span>{' '}
                  for those CRD activities and enter the prior-year CRD in
                  <span style={{ fontWeight: 600 }}> column (a),</span> then
                  complete columns{' '}
                  <span style={{ fontWeight: 600 }}>(b) and (c)</span> as
                  instructed.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (a): Loss
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>overall loss</span> for each
                  activity from{' '}
                  <span style={{ fontWeight: 600 }}>Part IV, column (e).</span>
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (b): Ratio
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Divide each activity’s loss by the{' '}
                    <span style={{ fontWeight: 600 }}>
                      total loss for all activities
                    </span>{' '}
                    to find its share. Enter this percentage in column (b).{' '}
                  </li>
                  <li>
                    All percentages in column (b) must{' '}
                    <span style={{ fontWeight: 600 }}>
                      add up to 1.00.
                    </span>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (c): Allocate Special Allowance
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  No prior year CRD:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply each ratio in column (b) by{' '}
                  <span style={{ fontWeight: 600 }}>Part II, line 9</span> of
                  Form 8582 and enter the result. The total of column (c) must
                  equal <span style={{ fontWeight: 600 }}>line 9.</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  With prior-year CRD:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    For{' '}
                    <span style={{ fontWeight: 600 }}>
                      current-year rental activities with active participation,
                    </span>{' '}
                    multiply each ratio by the{' '}
                    <span style={{ fontWeight: 600 }}>
                      lesser of line 4 or line 8.
                    </span>{' '}
                  </li>
                  <li>
                    For{' '}
                    <span style={{ fontWeight: 600 }}>
                      prior year unallowed CRD,
                    </span>{' '}
                    multiply each ratio by the amount from{' '}
                    <span style={{ fontWeight: 600 }}>
                      line D of the Worksheet.
                    </span>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Totals:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Column (c) for active rental activities = the lesser of line
                    4 or line 8.{' '}
                  </li>
                  <li>
                    Column (c) for prior-year CRD = amount from line D of the
                    Worksheet.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Total: Full Allowance Check
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If the{' '}
                    <span style={{ fontWeight: 600 }}>
                      total of column (c) equals the total of column (a),
                    </span>
                    the losses in Part IV (or, for prior-year CRD, the amount in
                    Part I, line 3) are{' '}
                    <span style={{ fontWeight: 600 }}>fully allowed</span> and
                    <span style={{ fontWeight: 600 }}>do not carry</span> over
                    to Part VII.{' '}
                  </li>
                  <li>
                    Report all amounts from{' '}
                    <span style={{ fontWeight: 600 }}>
                      columns (a), (b), and (c) of Part IV
                    </span>
                    on the appropriate{' '}
                    <span style={{ fontWeight: 600 }}>
                      forms and schedules.
                    </span>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Total: Partial Allowance
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If the{' '}
                    <span style={{ fontWeight: 600 }}>
                      total amount of column (c) is less than column (a),
                    </span>{' '}
                    the losses are{' '}
                    <span style={{ fontWeight: 600 }}>
                      only partially allowed.
                    </span>
                  </li>
                  <li>
                    Complete <span style={{ fontWeight: 600 }}>column (d)</span>{' '}
                    to calculate the remaining amounts.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (d): Loss after Special Allowance
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    Subtract <span style={{ fontWeight: 600 }}>column (c)</span>{' '}
                    from <span style={{ fontWeight: 600 }}>column (a)</span> and
                    enter the result in{' '}
                    <span style={{ fontWeight: 600 }}>column (d).</span>
                  </li>
                  <li>
                    Then carry this amount to{' '}
                    <span style={{ fontWeight: 600 }}>
                      Part VII, column (a).
                    </span>
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 25, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 84,
                  }}
                  id="part7"
                >
                  Part VII: Allocation of Unallowed Losses
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8582-part7-allocation-img.webp" />
                  <source srcSet="/assets/images/f8582-part7-allocation-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8582-part7-allocation-img.png"
                    loading="lazy"
                    alt="Form 8582 Part VII: Allocation of Unallowed Losses"
                    width={670}
                    height={171}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete <span style={{ fontWeight: 600 }}>Part VII</span> if
                  any activity shows an overall loss in{' '}
                  <span style={{ fontWeight: 600 }}>Part V, column (e)</span> or
                  a loss in{' '}
                  <span style={{ fontWeight: 600 }}>Part VI, column (d)</span>{' '}
                  (or Part IV and prior-year CRD in Part I, line 3, if Part VI
                  was not needed).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  List each activity’s{' '}
                  <span style={{ fontWeight: 600 }}>name</span> and the{' '}
                  <span style={{ fontWeight: 600 }}>
                    form, schedule, and line number
                  </span>{' '}
                  where the loss is reported.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the loss includes{' '}
                  <span style={{ fontWeight: 600 }}>
                    prior year unallowed CRD
                  </span>{' '}
                  (from non-rental or rental real estate activities), add{' '}
                  <span style={{ fontWeight: 600 }}>“CRD”</span> after the
                  activity name.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (a): Loss Amounts
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the losses from:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Column (d) of Part VI
                    </span>{' '}
                    (which includes column (e) of Part IV and any prior year
                    unallowed CRD from Part I, line 3, if Part VI wasn’t
                    needed), and
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Column (e) of Part V,
                    </span>{' '}
                    if any.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (b): Loss Allocation Ratio
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For each activity, divide its loss in{' '}
                  <span style={{ fontWeight: 600 }}>column (a)</span> by the{' '}
                  <span style={{ fontWeight: 600 }}>
                    total of all losses in column (a).
                  </span>{' '}
                  Enter this ratio in column (b).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The{' '}
                  <span style={{ fontWeight: 600 }}>
                    sum of all ratios must equal 1.00.
                  </span>
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (c): Compute Remaining Loss
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter{' '}
                    <span style={{ fontWeight: 600 }}>Part I, line 3</span> of
                    Form 8582 as a positive amount (Line A).
                  </li>
                  <li>
                    Enter{' '}
                    <span style={{ fontWeight: 600 }}>Part II, line 9</span> of
                    Form 8582 (Line B).
                  </li>
                  <li>
                    Subtract{' '}
                    <span style={{ fontWeight: 600 }}>Line B from Line A</span>{' '}
                    (Line C).{' '}
                  </li>
                  <li>
                    Multiply each ratio in{' '}
                    <span style={{ fontWeight: 600 }}>column (b)</span> by{' '}
                    <span style={{ fontWeight: 600 }}>Line C</span> and enter
                    the result in{' '}
                    <span style={{ fontWeight: 600 }}>column (c).</span>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 25, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 84,
                  }}
                  id="part8"
                >
                  Part VIII: Allowed Losses
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8582-part8-allowed-losses-img.webp" />
                  <source srcSet="/assets/images/f8582-part8-allowed-losses-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8582-part8-allowed-losses-img.png"
                    loading="lazy"
                    alt="Form 8582 Part VIII: Allowed Losses"
                    width={760}
                    height={199}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use <span style={{ fontWeight: 600 }}>Part VIII</span> for any
                  activity listed in Part VII{' '}
                  <span style={{ fontWeight: 600 }}>
                    if all the loss is reported on a single form or schedule
                  </span>{' '}
                  and no separate transactions need to be identified.
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      name of each activity
                    </span>{' '}
                    and the{' '}
                    <span style={{ fontWeight: 600 }}>
                      form, schedule, and line number
                    </span>{' '}
                    where the loss is reported.
                  </li>
                  <li>
                    If the activity includes{' '}
                    <span style={{ fontWeight: 600 }}>
                      prior-year unallowed CRD,
                    </span>{' '}
                    list it on a separate line and add{' '}
                    <span style={{ fontWeight: 600 }}>“CRD”</span> after the
                    activity name.
                  </li>
                  <li>
                    Even if part of the loss is current-year and part is
                    prior-year, use Part VIII to determine the{' '}
                    <span style={{ fontWeight: 600 }}>allowed loss</span>
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 0.5,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (a): Total Loss
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      net loss plus any prior year unallowed loss
                    </span>{' '}
                    for each activity.
                  </li>
                  <li>
                    {' '}
                    Add losses from{' '}
                    <span style={{ fontWeight: 600 }}>
                      columns (b) and (c) of Parts IV and V
                    </span>{' '}
                    and any prior-year CRD from{' '}
                    <span style={{ fontWeight: 600 }}>Part I, line 3.</span>
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 0.5,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (b): Unallowed Loss
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      amount from Part VII, column (c)
                    </span>{' '}
                    for each activity.
                  </li>
                  <li>
                    {' '}
                    These are your{' '}
                    <span style={{ fontWeight: 600 }}>
                      2025 unallowed losses.
                    </span>{' '}
                    Keep a record for next year’s PAL calculation.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 0.5,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Column (c): Allowed Loss
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Subtract{' '}
                    <span style={{ fontWeight: 600 }}>
                      column (b) from column (a).
                    </span>
                  </li>
                  <li>
                    {' '}
                    This is the{' '}
                    <span style={{ fontWeight: 600 }}>2025 allowed loss.</span>
                  </li>
                  <li>
                    Report these amounts on the appropriate{' '}
                    <span style={{ fontWeight: 600 }}>
                      forms and schedules,
                    </span>{' '}
                    following any additional limitations.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 25, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 84,
                  }}
                  id="part9"
                >
                  Part IX: Activities With Losses Reported on Two or More Forms
                  or Schedules
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8582-part9-activities-sch-img.webp" />
                  <source srcSet="/assets/images/f8582-part9-activities-sch-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8582-part9-activities-sch-img.png"
                    loading="lazy"
                    alt="Form 8582 Part IX: Activities with losses reported on two or more forms or schedules"
                    width={760}
                    height={529}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use <span style={{ fontWeight: 600 }}>Part IX</span> for any
                  activity listed in{' '}
                  <span style={{ fontWeight: 600 }}>Part VII</span> if its
                  losses are reported on{' '}
                  <span style={{ fontWeight: 600 }}>
                    more than one form or schedule
                  </span>{' '}
                  or must be
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    separately identified on the same form
                  </span>{' '}
                  (for example,{' '}
                  <span style={{ fontWeight: 600 }}>
                    28%-rate and non-28%-rate capital losses on Form 8949
                  </span>
                  ).
                </Typography>
                <Alert
                  severity="warning"
                  icon={<NoteAltIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Notes:</b> You can complete this section multiple times if
                  you have more than one activity.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Part IX divides the{' '}
                  <span style={{ fontWeight: 600 }}>
                    allowed and unallowed losses
                  </span>{' '}
                  for the activity and assigns the{' '}
                  <span style={{ fontWeight: 600 }}>allowed loss</span> to the
                  correct forms or schedules. Only losses that affect tax
                  liability differently are separated, including those reported
                  on:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Schedules C, E, and F,
                    </span>{' '}
                    and{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Form 8949,</span> Parts I
                    and II (28%-rate and non-28%-rate losses).{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<NoteAltIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Notes:</b>
                  <ul>
                    <li style={{ marginBottom: 6 }}>
                      Enter each transaction separately on{' '}
                      <span style={{ fontWeight: 600 }}>Form 8949</span> (see
                      instructions).{' '}
                    </li>
                    <li style={{ marginBottom: 6 }}>
                      Part IX also applies to{' '}
                      <span style={{ fontWeight: 600 }}>
                        Forms 4684, 4797, and 4835.
                      </span>{' '}
                    </li>
                    <li style={{ marginBottom: 6 }}>
                      Use a{' '}
                      <span style={{ fontWeight: 600 }}>separate Part IX</span>{' '}
                      for each activity reported on multiple forms or identified
                      separately.{' '}
                    </li>
                    <li>
                      List the{' '}
                      <span style={{ fontWeight: 600 }}>
                        form/schedule and line number
                      </span>{' '}
                      above each line 1a (e.g., Schedule D, line 12).{' '}
                    </li>
                  </ul>
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 0.5,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (a): Net loss plus prior year unallowed loss
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      total loss for the activity,
                    </span>{' '}
                    including any{' '}
                    <span style={{ fontWeight: 600 }}>
                      prior-year unallowed loss,
                    </span>{' '}
                    if it is reported on the{' '}
                    <span style={{ fontWeight: 600 }}>same form -</span> or, for
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Form 4797 or Form 8949,
                    </span>{' '}
                    the <span style={{ fontWeight: 600 }}>same part</span> of
                    the form.{' '}
                  </li>
                  <li>
                    If you have{' '}
                    <span style={{ fontWeight: 600 }}>both 28%</span> rate and
                    <span style={{ fontWeight: 600 }}>
                      non-28% rate losses
                    </span>{' '}
                    on Form 8949, review the{' '}
                    <span style={{ fontWeight: 600 }}>Form 8949 example</span>{' '}
                    before completing Part IX.{' '}
                  </li>
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    mb: 0.5,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (a): Net income from form or schedule
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter any{' '}
                    <span style={{ fontWeight: 600 }}>
                      net income from the activity
                    </span>{' '}
                    that is reported on the{' '}
                    <span style={{ fontWeight: 600 }}>
                      same form or schedule -
                    </span>{' '}
                    or the{' '}
                    <span style={{ fontWeight: 600 }}>
                      same part of the same form -
                    </span>{' '}
                    as the loss entered on line 1a, column (a).
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#c026521f',
                    borderLeft: '5px solid #c02652',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, fontStyle: 'italic' }}
                  >
                    Example:{' '}
                  </Typography>
                  <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                    <li>
                      If you report a prior-year unallowed loss from Form 4797,
                      Part I on line 1a and the activity has a current-year gain
                      on the same form and part, enter that gain on line 1b,
                      column (a).{' '}
                    </li>
                    <li>
                      If there is no gain, enter 0 on line 1b, column (a).{' '}
                    </li>
                  </Box>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 0.5,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                  }}
                >
                  Line 1c
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  On <span style={{ fontWeight: 600 }}>line 1c,</span> subtract
                  the income on <span style={{ fontWeight: 600 }}>line 1b</span>{' '}
                  from the total loss shown on{' '}
                  <span style={{ fontWeight: 600 }}>line 1a.</span> If the
                  result is{' '}
                  <span style={{ fontWeight: 600 }}>
                    zero or a negative number,
                  </span>{' '}
                  enter <span style={{ fontWeight: 600 }}>0.</span> This line
                  shows how much loss remains after offsetting any income from
                  the activity
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (b): Net income from form or schedule
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Subtract{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      line 1b, column (a)
                    </span>{' '}
                    from{' '}
                    <span style={{ fontWeight: 600 }}>
                      line 1a, column (a)
                    </span>{' '}
                    and enter the result in{' '}
                    <span style={{ fontWeight: 600 }}>column (b).</span>
                  </li>
                  <li>
                    If{' '}
                    <span style={{ fontWeight: 600 }}>line 1b, column (a)</span>{' '}
                    is greater than{' '}
                    <span style={{ fontWeight: 600 }}>
                      line 1a, column (a),
                    </span>{' '}
                    enter <span style={{ fontWeight: 600 }}>0</span> in{' '}
                    <span style={{ fontWeight: 600 }}>column (b).</span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (c): Ratio
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Divide each loss in{' '}
                    <span style={{ fontWeight: 600 }}>column (b)</span> by the
                    total of <span style={{ fontWeight: 600 }}>column (b)</span>
                    and enter the result as a ratio.
                  </li>
                  <li>
                    All ratios in this column must add up to{' '}
                    <span style={{ fontWeight: 600 }}>1.00. </span>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (d): Unallowed loss
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Multiply the{' '}
                    <span style={{ fontWeight: 600 }}>unallowed loss</span> from
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Part VII, column (c)
                    </span>{' '}
                    by each ratio in{' '}
                    <span style={{ fontWeight: 600 }}> column (c) </span> of
                    Part IX.
                  </li>
                  <li>
                    If <span style={{ fontWeight: 600 }}>column (b)</span> shows{' '}
                    <span style={{ fontWeight: 600 }}>0,</span> enter 0 in{' '}
                    <span style={{ fontWeight: 600 }}>column (d)</span> for that
                    form or schedule.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (e): Allowed loss
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract <span style={{ fontWeight: 600 }}>column (d)</span>{' '}
                  from the loss shown on{' '}
                  <span style={{ fontWeight: 600 }}>line 1a, column (a).</span>{' '}
                  The result is the{' '}
                  <span style={{ fontWeight: 600 }}>loss allowed for 2025</span>{' '}
                  under the passive loss rules.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report this allowed loss on the appropriate forms or
                  schedules, following any other applicable limitations. The
                  forms must reflect both:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    The allowed loss from{' '}
                    <span style={{ fontWeight: 600 }}>column (e),</span>{' '}
                    and{' '}
                  </li>
                  <li>
                    Any related income for the activity from{' '}
                    <span style={{ fontWeight: 600 }}>
                      Part IV or Part V, column (a).
                    </span>
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    textAlign: 'left',
                  }}
                >
                  Parts VIII and IX: Allowed vs. Unallowed Losses
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Parts <span style={{ fontWeight: 600 }}>VIII and IX</span> are
                  used to determine the{' '}
                  <span style={{ fontWeight: 600 }}>
                    allowed and unallowed losses
                  </span>{' '}
                  for each activity.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Use <span style={{ fontWeight: 600 }}>Part VIII</span> when
                    all losses from an activity are reported on{' '}
                    <span style={{ fontWeight: 600 }}>
                      one form or schedule,
                    </span>{' '}
                    and no separate transactions need to be identified.{' '}
                  </li>
                  <li>
                    Use <span style={{ fontWeight: 600 }}>Part IX</span> if an
                    activity’s losses are reported on{' '}
                    <span style={{ fontWeight: 600 }}>
                      more than one form or schedule,
                    </span>{' '}
                    or if the activity includes{' '}
                    <span style={{ fontWeight: 600 }}>
                      multiple transactions that must be reported separately
                    </span>{' '}
                    (such as certain capital losses taxed at different
                    rates).{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<NoteAltIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Notes:</b>
                  <ul>
                    <li style={{ marginBottom: 6 }}>
                      Gains or losses taxed at the 28% rate include those from
                      collectibles, certain long-term losses, and Section 1202
                      qualified for small business stock sales.
                    </li>
                    <li style={{ marginBottom: 0 }}>
                      See the Schedule D instructions for full details.
                    </li>
                  </ul>
                </Alert>

                <Alert
                  severity="info"
                  icon={false}
                  sx={{ borderRadius: 2, scrollMarginTop: 88 }}
                  id="add"
                >
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        This information is based on IRS guidance. Refer to the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i8582.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        Instructions for Form 8582, Passive Activity Loss
                        Limitations, to determine the calculation and reporting
                        of allowed and disallowed passive activity losses.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to complete your Form 5227 e-filing?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Ensure compliance with IRS passive activity loss regulations.
              TaxZerone provides a secure
              <br />
              and efficient way to attach IRS Form 8582 and electronically file
              Form 5227 with accuracy and confidence.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8582instruction`}
              >
                E-file Form 5227 Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form8582Instructions;
